import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SchemaOrg from '../../components/feature/SchemaOrg';
import { categories, culinaryLivingProducts, healthFitnessWellnessProducts } from '../../mocks/categories';

// Define the CategoryProduct type
interface CategoryProduct {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  link: string;
}

// Define Review interface for subcategory reviews
interface Review {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  rating: number;
  price: string;
}

// Define Subcategory interface
interface Subcategory {
  name: string;
  reviews: Review[];
}

// Define CategoryData interface
interface CategoryData {
  [key: string]: {
    title: string;
    description: string;
    icon: string;
    subcategories: Subcategory[];
  };
}

const categoryProducts: Record<string, CategoryProduct[]> = {
  'smart-betting-strategies': [
    // Football
    {
      id: 'the-lay-formula',
      title: 'The Lay Formula',
      description: 'Professional football betting system using lay betting strategies to minimize risk and maximize profits with proven mathematical formulas',
      image: 'https://public.readdy.ai/ai/img_res/486024405f60b8c1a53f77c303f69efe.jpg',
      category: 'Football',
      link: '/reviews/the-lay-formula-review',
    },
    {
      id: 'daily-football-winners',
      title: 'Daily Football Winners',
      description: 'Expert football tips and predictions delivered daily with detailed analysis, statistics, and winning strategies for consistent profits',
      image: 'https://readdy.ai/api/search-image?query=Daily%20football%20betting%20tips%20notification%20on%20smartphone%20with%20winning%20predictions%20green%20checkmarks%20and%20match%20schedules%20modern%20sports%20betting%20app%20interface%20with%20trophy%20icon%20and%20success%20indicators&width=800&height=600&seq=daily-football-002&orientation=landscape',
      category: 'Football',
      link: '/reviews/daily-football-winners-review',
    },
    {
      id: 'profit-maximiser',
      title: 'Profit Maximiser',
      description: 'Complete matched betting system for football with step-by-step tutorials, odds matching software, and guaranteed profit strategies',
      image: 'https://readdy.ai/api/search-image?query=Matched%20betting%20profit%20calculator%20showing%20guaranteed%20returns%20with%20football%20odds%20comparison%20tables%20and%20step%20by%20step%20tutorial%20interface%20professional%20betting%20software%20dashboard%20with%20rising%20profit%20graphs&width=800&height=600&seq=profit-max-003&orientation=landscape',
      category: 'Football',
      link: '/reviews/profit-maximiser-review',
    },
    {
      id: 'bank-on-football',
      title: 'Bank On Football',
      description: 'Premium football betting service with insider tips, expert analysis, and proven strategies for building your betting bank consistently',
      image: 'https://readdy.ai/api/search-image?query=Premium%20football%20betting%20service%20interface%20showing%20expert%20analysis%20reports%20insider%20tips%20and%20growing%20betting%20bank%20balance%20with%20professional%20charts%20and%20football%20field%20background%20sophisticated%20design&width=800&height=600&seq=bank-on-football-004&orientation=landscape',
      category: 'Football',
      link: '/reviews/bank-on-football-review',
    },
    // Horse Racing
    {
      id: '7-bets-4-free',
      title: '7 Bets 4 Free',
      description: 'Exclusive horse racing tips service offering seven carefully selected bets with four free selections, backed by expert handicapping analysis',
      image: 'https://readdy.ai/api/search-image?query=Horse%20racing%20betting%20tips%20service%20showing%20seven%20race%20selections%20with%20expert%20handicapping%20analysis%20racing%20horses%20on%20track%20professional%20tipster%20interface%20with%20winning%20statistics%20and%20free%20bet%20offers&width=800&height=600&seq=seven-bets-005&orientation=landscape',
      category: 'Horse Racing',
      link: '/reviews/7-bets-4-free-review',
    },
    {
      id: 'breakthrough-bets',
      title: 'Breakthrough Bets',
      description: 'Revolutionary horse racing system using advanced algorithms and historical data analysis to identify breakthrough betting opportunities',
      image: 'https://readdy.ai/api/search-image?query=Advanced%20horse%20racing%20algorithm%20software%20displaying%20breakthrough%20betting%20opportunities%20with%20historical%20data%20charts%20racing%20analytics%20and%20predictive%20models%20modern%20technology%20interface%20with%20thoroughbred%20horses&width=800&height=600&seq=breakthrough-006&orientation=landscape',
      category: 'Horse Racing',
      link: '/reviews/breakthrough-bets-review',
    },
    {
      id: 'value-multiplier',
      title: 'Value Multiplier',
      description: 'Professional value betting system for horse racing that identifies undervalued horses and multiplies your betting returns through smart analysis',
      image: 'https://readdy.ai/api/search-image?query=Value%20betting%20calculator%20for%20horse%20racing%20showing%20undervalued%20odds%20analysis%20with%20multiplier%20charts%20and%20smart%20betting%20returns%20interface%20professional%20racing%20data%20dashboard%20with%20winning%20horse%20silhouettes&width=800&height=600&seq=value-multiplier-007&orientation=landscape',
      category: 'Horse Racing',
      link: '/reviews/value-multiplier-review',
    },
    {
      id: 'betting-scientist',
      title: 'Betting Scientist',
      description: 'Scientific approach to horse racing betting using statistical analysis, machine learning algorithms, and data-driven predictions for consistent wins',
      image: 'https://readdy.ai/api/search-image?query=Scientific%20horse%20racing%20analysis%20software%20with%20statistical%20models%20machine%20learning%20algorithms%20and%20data%20visualization%20charts%20laboratory%20style%20interface%20with%20racing%20data%20and%20predictive%20analytics%20professional%20scientific%20design&width=800&height=600&seq=betting-scientist-008&orientation=landscape',
      category: 'Horse Racing',
      link: '/reviews/betting-scientist-review',
    },
    // Soccer
    {
      id: 'new-football-booster-tips',
      title: 'New Football Booster Tips',
      description: 'Enhanced football betting tips service with boosted odds, accumulator strategies, and expert predictions for maximum returns',
      image: 'https://readdy.ai/api/search-image?query=Football%20accumulator%20betting%20tips%20showing%20boosted%20odds%20and%20enhanced%20predictions%20with%20soccer%20ball%20and%20multiple%20match%20selections%20modern%20sports%20betting%20interface%20with%20rocket%20boost%20icon%20and%20winning%20streaks&width=800&height=600&seq=booster-tips-009&orientation=landscape',
      category: 'Soccer',
      link: '/reviews/football-booster-tips-review',
    },
    {
      id: 'goal-predictor',
      title: 'Goal Predictor',
      description: 'Advanced soccer prediction system specializing in goal forecasting using statistical models and team performance analysis for accurate betting',
      image: 'https://readdy.ai/api/search-image?query=Soccer%20goal%20prediction%20software%20displaying%20statistical%20forecasting%20models%20with%20team%20performance%20graphs%20and%20accurate%20betting%20predictions%20modern%20analytics%20dashboard%20with%20soccer%20goal%20net%20and%20data%20visualization&width=800&height=600&seq=goal-predictor-010&orientation=landscape',
      category: 'Soccer',
      link: '/reviews/goal-predictor-review',
    },
    // General
    {
      id: 'zcode-system',
      title: 'Zcode System',
      description: 'Comprehensive sports betting system covering multiple sports with AI-powered predictions, community insights, and proven winning strategies',
      image: 'https://readdy.ai/api/search-image?query=Multi%20sport%20betting%20system%20dashboard%20with%20AI%20powered%20predictions%20showing%20various%20sports%20icons%20basketball%20football%20baseball%20with%20community%20insights%20and%20winning%20statistics%20futuristic%20technology%20interface&width=800&height=600&seq=zcode-system-011&orientation=landscape',
      category: 'General',
      link: '/reviews/zcode-system-review',
    },
    {
      id: 'betting-gods',
      title: 'Betting Gods',
      description: 'Premium tipster marketplace connecting you with verified expert bettors across all sports with transparent performance tracking and results',
      image: 'https://readdy.ai/api/search-image?query=Premium%20tipster%20marketplace%20platform%20showing%20verified%20expert%20profiles%20with%20performance%20tracking%20charts%20and%20transparent%20results%20multiple%20sports%20betting%20experts%20professional%20marketplace%20interface%20with%20gold%20accents&width=800&height=600&seq=betting-gods-012&orientation=landscape',
      category: 'General',
      link: '/reviews/betting-gods-review',
    },
    {
      id: 'lottery-maximizer',
      title: 'Lottery Maximizer',
      description: 'Strategic lottery system using mathematical patterns and probability analysis to maximize your chances of winning lottery jackpots',
      image: 'https://readdy.ai/api/search-image?query=Lottery%20number%20analysis%20system%20showing%20mathematical%20patterns%20and%20probability%20calculations%20with%20lottery%20balls%20and%20winning%20number%20combinations%20strategic%20interface%20with%20jackpot%20symbols%20and%20statistical%20charts&width=800&height=600&seq=lottery-maximizer-013&orientation=landscape',
      category: 'General',
      link: '/reviews/lottery-maximizer-review',
    },
    {
      id: 'exterminator-system',
      title: 'The Exterminator Sports Betting System',
      description: 'Aggressive sports betting system designed to eliminate losses and dominate bookmakers with high-stakes strategies and winning formulas',
      image: 'https://readdy.ai/api/search-image?query=Aggressive%20sports%20betting%20strategy%20interface%20with%20high%20stakes%20winning%20formulas%20and%20dominating%20performance%20charts%20powerful%20design%20with%20multiple%20sports%20elements%20and%20victory%20symbols%20professional%20betting%20system%20dashboard&width=800&height=600&seq=exterminator-system-014&orientation=landscape',
      category: 'General',
      link: '/reviews/exterminator-system-review',
    },
  ],
};

// Subcategories for Culture & Creativity
const cultureCreativitySubcategories = [
  {
    name: 'Music',
    slug: 'music',
    icon: 'ri-music-2-line',
    description: 'Learn to play instruments and master music theory',
    reviews: [
      {
        title: 'Pianoforall Review',
        slug: 'pianoforall-review',
        excerpt: 'Comprehensive piano learning course with 10 interactive eBooks covering multiple styles',
        image: 'https://readdy.ai/api/search-image?query=modern%20digital%20piano%20keyboard%20with%20sheet%20music%20and%20headphones%20on%20wooden%20desk%20warm%20studio%20lighting%20minimalist%20background%20professional%20music%20learning%20setup&width=400&height=300&seq=piano001&orientation=landscape'
      }
    ]
  },
  {
    name: 'Art',
    slug: 'art',
    icon: 'ri-palette-line',
    description: 'Discover unique art pieces and collectibles',
    reviews: [
      {
        title: 'Liberty Token Review',
        slug: 'liberty-token-review',
        excerpt: 'Handcrafted commemorative tokens celebrating American freedom and heritage',
        image: 'https://i0.wp.com/thelibertytoken.com/wp-content/uploads/2025/11/liberty-newform.png?fit=300%2C300&ssl=1'
      }
    ]
  },
  {
    name: 'Body Art',
    slug: 'body-art',
    icon: 'ri-brush-line',
    description: 'Tattoo care, removal, and body art techniques',
    reviews: [
      {
        title: 'Laserless Tattoo Removal Review',
        slug: 'laserless-tattoo-removal-review',
        excerpt: 'Natural tattoo removal methods without expensive laser treatments',
        image: 'https://readdy.ai/api/search-image?query=natural%20skincare%20cream%20application%20on%20arm%20with%20fading%20tattoo%20gentle%20removal%20process%20clean%20white%20background%20medical%20spa%20aesthetic%20soft%20natural%20lighting&width=400&height=300&seq=tattoo001&orientation=landscape'
      }
    ]
  }
];

// Subcategories for Smart Betting Strategies
const smartBettingSubcategories = [
  {
    name: 'Football',
    slug: 'football',
    icon: 'ri-football-line',
    description: 'Expert football betting strategies and systems',
    reviews: [
      {
        title: 'The Lay Formula Review',
        slug: 'the-lay-formula-review',
        excerpt: 'Revolutionary lay betting system for football with proven track record and risk management strategies',
        image: 'https://readdy.ai/api/search-image?query=Professional%20football%20betting%20strategy%20dashboard%20showing%20mathematical%20formulas%20and%20lay%20betting%20calculations%20on%20computer%20screen%20with%20green%20profit%20charts%20and%20football%20match%20statistics%20clean%20modern%20interface&width=800&height=600&seq=lay-formula-001&orientation=landscape'
      },
      {
        title: 'Daily Football Winners Review',
        slug: 'daily-football-winners-review',
        excerpt: 'Daily football betting tips with high success rate and comprehensive match analysis',
        image: 'https://readdy.ai/api/search-image?query=Daily%20football%20betting%20tips%20notification%20on%20smartphone%20with%20winning%20predictions%20green%20checkmarks%20and%20match%20schedules%20modern%20sports%20betting%20app%20interface%20with%20trophy%20icon%20and%20success%20indicators&width=800&height=600&seq=daily-football-002&orientation=landscape'
      },
      {
        title: 'Profit Maximiser Review',
        slug: 'profit-maximiser-review',
        excerpt: 'Complete matched betting service with football focus and guaranteed profit strategies',
        image: 'https://readdy.ai/api/search-image?query=Matched%20betting%20profit%20calculator%20showing%20guaranteed%20returns%20with%20football%20odds%20comparison%20tables%20and%20step%20by%20step%20tutorial%20interface%20professional%20betting%20software%20dashboard%20with%20rising%20profit%20graphs&width=800&height=600&seq=profit-max-003&orientation=landscape'
      },
      {
        title: 'Bank On Football Review',
        slug: 'bank-on-football-review',
        excerpt: 'Reliable football betting system with consistent returns and expert analysis',
        image: 'https://readdy.ai/api/search-image?query=Premium%20football%20betting%20service%20interface%20showing%20expert%20analysis%20reports%20insider%20tips%20and%20growing%20betting%20bank%20balance%20with%20professional%20charts%20and%20football%20field%20background%20sophisticated%20design&width=800&height=600&seq=bank-on-football-004&orientation=landscape'
      }
    ]
  },
  {
    name: 'Horse Racing',
    slug: 'horse-racing',
    icon: 'ri-trophy-line',
    description: 'Professional horse racing betting systems',
    reviews: [
      {
        title: '7 Bets 4 Free Review',
        slug: '7-bets-4-free-review',
        excerpt: 'Innovative horse racing betting system offering free bets and consistent winning strategies',
        image: 'https://readdy.ai/api/search-image?query=Horse%20racing%20betting%20tips%20service%20showing%20seven%20race%20selections%20with%20expert%20handicapping%20analysis%20racing%20horses%20on%20track%20professional%20tipster%20interface%20with%20winning%20statistics%20and%20free%20bet%20offers&width=800&height=600&seq=seven-bets-005&orientation=landscape'
      },
      {
        title: 'Breakthrough Bets Review',
        slug: 'breakthrough-bets-review',
        excerpt: 'Advanced horse racing system using hypnotic techniques and proven selection methods',
        image: 'https://readdy.ai/api/search-image?query=Advanced%20horse%20racing%20algorithm%20software%20displaying%20breakthrough%20betting%20opportunities%20with%20historical%20data%20charts%20racing%20analytics%20and%20predictive%20models%20modern%20technology%20interface%20with%20thoroughbred%20horses&width=800&height=600&seq=breakthrough-006&orientation=landscape'
      },
      {
        title: 'Value Multiplier Review',
        slug: 'value-multiplier-review',
        excerpt: 'Professional value betting system for horse racing with proven ROI and expert selection strategies',
        image: 'https://readdy.ai/api/search-image?query=Value%20betting%20calculator%20for%20horse%20racing%20showing%20undervalued%20odds%20analysis%20with%20multiplier%20charts%20and%20smart%20betting%20returns%20interface%20professional%20racing%20data%20dashboard%20with%20winning%20horse%20silhouettes&width=800&height=600&seq=value-multiplier-007&orientation=landscape'
      },
      {
        title: 'Betting Scientist Review',
        slug: 'betting-scientist-review',
        excerpt: 'Scientific approach to horse racing betting using statistical analysis and machine learning algorithms',
        image: 'https://readdy.ai/api/search-image?query=Scientific%20horse%20racing%20analysis%20software%20with%20statistical%20models%20machine%20learning%20algorithms%20and%20data%20visualization%20charts%20laboratory%20style%20interface%20with%20racing%20data%20and%20predictive%20analytics%20professional%20scientific%20design&width=800&height=600&seq=betting-scientist-008&orientation=landscape'
      }
    ]
  },
  {
    name: 'Soccer',
    slug: 'soccer',
    icon: 'ri-ball-pen-line',
    description: 'Soccer betting tips and winning strategies',
    reviews: [
      {
        title: 'New Football Booster Tips Review',
        slug: 'football-booster-tips-review',
        excerpt: 'Premium soccer betting tips service with high strike rate and detailed match insights',
        image: 'https://readdy.ai/api/search-image?query=Football%20accumulator%20betting%20tips%20showing%20boosted%20odds%20and%20enhanced%20predictions%20with%20soccer%20ball%20and%20multiple%20match%20selections%20modern%20sports%20betting%20interface%20with%20rocket%20boost%20icon%20and%20winning%20streaks&width=800&height=600&seq=booster-tips-009&orientation=landscape'
      },
      {
        title: 'Goal Predictor Review',
        slug: 'goal-predictor-review',
        excerpt: 'Advanced soccer prediction system for accumulator bets with proven goal forecasting methodology',
        image: 'https://readdy.ai/api/search-image?query=Soccer%20goal%20prediction%20software%20displaying%20statistical%20forecasting%20models%20with%20team%20performance%20graphs%20and%20accurate%20betting%20predictions%20modern%20analytics%20dashboard%20with%20soccer%20goal%20net%20and%20data%20visualization&width=800&height=600&seq=goal-predictor-010&orientation=landscape'
      }
    ]
  },
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-line-chart-line',
    description: 'Multi-sport betting systems and strategies',
    reviews: [
      {
        title: 'Zcode System Review',
        slug: 'zcode-system-review',
        excerpt: 'Comprehensive sports betting prediction system covering multiple sports with AI-powered analysis',
        image: 'https://readdy.ai/api/search-image?query=Multi%20sport%20betting%20system%20dashboard%20with%20AI%20powered%20predictions%20showing%20various%20sports%20icons%20basketball%20football%20baseball%20with%20community%20insights%20and%20winning%20statistics%20futuristic%20technology%20interface&width=800&height=600&seq=zcode-system-011&orientation=landscape'
      },
      {
        title: 'Betting Gods Review',
        slug: 'betting-gods-review',
        excerpt: 'Premium tipster platform connecting you with professional betting experts across all sports',
        image: 'https://readdy.ai/api/search-image?query=Premium%20tipster%20marketplace%20platform%20showing%20verified%20expert%20profiles%20with%20performance%20tracking%20charts%20and%20transparent%20results%20multiple%20sports%20betting%20experts%20professional%20marketplace%20interface%20with%20gold%20accents&width=800&height=600&seq=betting-gods-012&orientation=landscape'
      },
      {
        title: 'Lottery Maximizer Review',
        slug: 'lottery-maximizer-review',
        excerpt: 'Strategic lottery system using mathematical patterns and probability analysis to maximize your chances of winning lottery jackpots',
        image: 'https://readdy.ai/api/search-image?query=Lottery%20number%20analysis%20system%20showing%20mathematical%20patterns%20and%20probability%20calculations%20with%20lottery%20balls%20and%20winning%20number%20combinations%20strategic%20interface%20with%20jackpot%20symbols%20and%20statistical%20charts&width=800&height=600&seq=lottery-maximizer-013&orientation=landscape'
      },
      {
        title: 'The Exterminator Sports Betting System Review',
        slug: 'exterminator-system-review',
        excerpt: 'Powerful multi-sport betting system with proven results and comprehensive tracking tools',
        image: 'https://readdy.ai/api/search-image?query=Aggressive%20sports%20betting%20strategy%20interface%20with%20high%20stakes%20winning%20formulas%20and%20dominating%20performance%20charts%20powerful%20design%20with%20multiple%20sports%20elements%20and%20victory%20symbols%20professional%20betting%20system%20dashboard&width=800&height=600&seq=exterminator-system-014&orientation=landscape'
      }
    ]
  }
];

// Subcategories for Business & Wealth
const businessWealthSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-line-chart-line',
    description: 'Trading, investing, and wealth-building strategies',
    reviews: [
      {
        title: 'VIP Indicators Review',
        slug: 'vip-indicators-review',
        excerpt: 'AI-powered trading indicators for Forex, crypto, and stocks with 93% accuracy and 24/7 signal alerts',
        image: 'https://www.vipindicators.com/hosted/images/d6/a5a66d36d545a78f12f702c1d49ffa/blanked-vip-18.png'
      }
    ]
  },
  {
    name: 'Small Biz / Entrepreneurship',
    slug: 'small-biz-entrepreneurship',
    icon: 'ri-store-3-line',
    description: 'Start and grow your own business',
    reviews: [
      {
        title: 'WoodProfits Review',
        slug: 'woodprofits-review',
        excerpt: 'Complete guide to starting a profitable home woodworking business with 500+ proven designs and personal coaching',
        image: 'https://www.woodprofits.com/wp-content/uploads/2023/05/wpbook61.png'
      }
    ]
  },
  {
    name: 'Personal Finance',
    slug: 'personal-finance',
    icon: 'ri-money-dollar-circle-line',
    description: 'Manage money, find grants, and build financial security',
    reviews: [
      {
        title: 'LeskoHelp Review',
        slug: 'leskohelp-review',
        excerpt: 'Access 600,000+ government grant programs with expert guidance from Matthew Lesko and daily support',
        image: 'https://www.free.lesko.com/hosted/images/50/71ac89011249cb89ad98dd1776b39e/Lesko-Help-Mockups.png'
      }
    ]
  },
  {
    name: 'Real Estate',
    slug: 'real-estate',
    icon: 'ri-building-line',
    description: 'Real estate investing and property business strategies',
    reviews: [
      {
        title: 'Business Credit Builder Review',
        slug: 'business-credit-builder-review',
        excerpt: 'Comprehensive program teaching entrepreneurs how to establish and leverage business credit for real estate investing and business growth',
        image: 'https://readdy.ai/api/search-image?query=Professional%20business%20credit%20score%20dashboard%20showing%20excellent%20credit%20rating%20with%20financial%20charts%20real%20estate%20property%20images%20and%20business%20documents%20modern%20clean%20interface%20with%20green%20approval%20checkmarks%20and%20rising%20credit%20score%20graph&width=800&height=600&seq=business-credit-builder-001&orientation=landscape'
      },
      {
        title: 'MicroBanking Method Review',
        slug: 'microbanking-method-review',
        excerpt: 'Learn creative real estate financing strategies to fund property deals without traditional banks or personal credit',
        image: 'https://readdy.ai/api/search-image?query=Creative%20real%20estate%20financing%20concept%20with%20residential%20properties%20and%20alternative%20funding%20sources%20modern%20investment%20strategy%20illustration%20showing%20property%20deals%20without%20traditional%20banks%20professional%20financial%20planning%20aesthetic&width=800&height=600&seq=microbanking-method-001&orientation=landscape'
      }
    ]
  },
  {
    name: 'Derivatives',
    slug: 'derivatives',
    icon: 'ri-stock-line',
    description: 'Options trading and derivatives strategies',
    reviews: [
      {
        title: 'Options Pop Review',
        slug: 'options-pop-review',
        excerpt: 'Comprehensive options trading system teaching proven strategies for generating consistent income in various market conditions',
        image: 'https://readdy.ai/api/search-image?query=Professional%20options%20trading%20platform%20interface%20showing%20call%20and%20put%20options%20chains%20with%20strike%20prices%20expiration%20dates%20and%20profit%20loss%20graphs%20modern%20trading%20dashboard%20with%20green%20buy%20signals&width=800&height=600&seq=options-pop-001&orientation=landscape'
      }
    ]
  },
  {
    name: 'Equities & Stocks',
    slug: 'equities-stocks',
    icon: 'ri-line-chart-line',
    description: 'Stock trading systems and equity strategies',
    reviews: [
      {
        title: 'SPY Sniper Trading System Review',
        slug: 'spy-sniper-trading-system-review',
        excerpt: 'Precision trading program teaching traders how to profit from SPY options using proven technical analysis and timing strategies',
        image: 'https://readdy.ai/api/search-image?query=SPY%20stock%20chart%20with%20precision%20trading%20signals%20showing%20sniper%20crosshair%20targeting%20entry%20points%20on%20candlestick%20chart%20with%20technical%20indicators%20moving%20averages%20and%20profit%20zones%20professional%20trading%20platform%20interface%20with%20green%20buy%20signals&width=800&height=600&seq=spy-sniper-trading-system-001&orientation=landscape'
      }
    ]
  },
  {
    name: 'Careers, Industries & Professions',
    slug: 'careers-industries-professions',
    icon: 'ri-briefcase-line',
    description: 'Professional skills and niche business opportunities',
    reviews: [
      {
        title: 'Bake-A-Dog-A-Bone Review',
        slug: 'bake-a-dog-a-bone-review',
        excerpt: 'Complete guide to starting a profitable homemade dog treat business with proven recipes and marketing strategies',
        image: 'https://readdy.ai/api/search-image?query=homemade%20dog%20treats%20on%20rustic%20wooden%20table%20with%20golden%20retriever%20watching%20happily%20professional%20bakery%20setup%20with%20bone%20shaped%20cookies%20and%20natural%20ingredients%20warm%20kitchen%20lighting%20cozy%20home%20business%20atmosphere&width=800&height=600&seq=bake-a-dog-a-bone-001&orientation=landscape'
      }
    ]
  }
];

// Subcategories for Careers & Work Life
const careersWorkLifeSubcategories = [
  {
    name: 'Training',
    slug: 'training',
    icon: 'ri-graduation-cap-line',
    description: 'Career training programs and professional development',
    reviews: [
      {
        title: 'Medical Courier Jobs Review',
        slug: 'medical-courier-jobs-review',
        excerpt: 'Earn up to $5,000 per month driving your own vehicle with comprehensive medical courier training',
        image: 'https://readdy.ai/api/search-image?query=professional%20medical%20courier%20driver%20in%20blue%20uniform%20holding%20red%20medical%20delivery%20bag%20standing%20next%20to%20vehicle%20smiling%20confidently%20with%20medical%20transport%20equipment%20clean%20bright%20professional%20photography%20high%20quality%20realistic&width=800&height=600&seq=medical-courier-jobs-001&orientation=landscape'
      }
    ]
  }
];

// Subcategories for Eco Living & Sustainability
const ecoLivingSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-leaf-line',
    description: 'Comprehensive eco-living solutions and sustainable lifestyle guides',
    reviews: [
      {
        title: 'Energy Liberation Army Review',
        slug: 'energy-liberation-army-review',
        excerpt: 'Paul Pantone\'s water-powered hydrogen generator - build for under $50 and slash electricity bills by 75% or more',
        image: 'https://energyliberationarmy.com/wp-content/uploads/2019/10/ALL2.png'
      }
    ]
  },
  {
    name: 'Alternative Energy',
    slug: 'alternative-energy',
    icon: 'ri-flashlight-line',
    description: 'Renewable energy solutions and sustainable power systems',
    reviews: [
      {
        title: 'Energy Revolution System Review',
        slug: 'energy-revolution-system-review',
        excerpt: 'Learn how to build your own renewable energy system and slash electricity bills with proven DIY methods',
        image: 'https://readdy.ai/api/search-image?query=modern%20home%20solar%20panel%20installation%20on%20residential%20rooftop%20with%20wind%20turbine%20in%20background%20renewable%20energy%20system%20clean%20sustainable%20power%20generation%20bright%20sunny%20day%20professional%20photography%20realistic%20high%20quality&width=800&height=600&seq=energy-revolution-system-001&orientation=landscape'
      },
      {
        title: 'The Ultimate Off-Grid Generator Review',
        slug: 'ultimate-off-grid-generator-review',
        excerpt: 'Build a Tesla-inspired flywheel generator for under $200 and cut electricity bills by 50-80% with this revolutionary DIY system',
        image: 'https://readdy.ai/api/search-image?query=DIY%20flywheel%20generator%20device%20on%20workbench%20with%20tools%20and%20blueprints%20Tesla%20inspired%20alternative%20energy%20system%20compact%20home%20power%20generation%20setup%20professional%20workshop%20photography%20realistic%20high%20quality&width=800&height=600&seq=ultimate-off-grid-generator-001&orientation=landscape'
      },
      {
        title: 'Home Power Shield Review',
        slug: 'home-power-shield-review',
        excerpt: 'NASA-developed pocket-sized flywheel generator you can build in 2-3 hours for $200 to power your entire home',
        image: 'https://readdy.ai/api/search-image?query=compact%20portable%20flywheel%20generator%20device%20in%20hand%20showing%20small%20size%20NASA%20technology%20inspired%20home%20power%20system%20modern%20clean%20energy%20solution%20professional%20product%20photography%20realistic%20high%20quality&width=800&height=600&seq=home-power-shield-001&orientation=landscape'
      },
      {
        title: 'The KINETIC Power System Review',
        slug: 'kinetic-power-system-review',
        excerpt: 'Build a 1927 aviation-inspired flywheel generator for under $200 and slash electricity bills by 50-67% with this proven DIY energy system',
        image: 'https://readdy.ai/api/search-image?query=vintage%201927%20KINETIC%20generator%20device%20on%20workbench%20with%20tools%20and%20blueprints%20aviation%20inspired%20flywheel%20technology%20alternative%20energy%20system%20DIY%20home%20power%20generation%20professional%20workshop%20photography%20realistic%20high%20quality&width=800&height=600&seq=kinetic-power-system-001&orientation=landscape'
      },
      {
        title: 'Solar Innovator Review',
        slug: 'solar-innovator-review',
        excerpt: 'Build a revolutionary 3D solar sphere for under $100 that generates 101% more power than traditional panels on just 12 sqft of space',
        image: 'https://readdy.ai/api/search-image?query=innovative%203D%20solar%20sphere%20device%20compact%20spherical%20solar%20panel%20design%20nature%20inspired%20renewable%20energy%20system%20small%20space%20installation%20professional%20product%20photography%20realistic%20high%20quality&width=800&height=600&seq=solar-innovator-001&orientation=landscape'
      },
      {
        title: 'Hendershot Power Generator Review',
        slug: 'hendershot-power-generator-review',
        excerpt: 'Build the historic 1927 free energy generator that powered Lindbergh\'s Atlantic flight for under $78 and cut electricity bills by 75-90%',
        image: 'https://readdy.ai/api/search-image?query=historic%201927%20Hendershot%20generator%20device%20vintage%20electronics%20components%20free%20energy%20system%20DIY%20home%20power%20generation%20retro%20technology%20professional%20photography%20realistic%20high%20quality&width=800&height=600&seq=hendershot-power-generator-001&orientation=landscape'
      },
      {
        title: 'Easy DIY Power Plan Review',
        slug: 'easy-diy-power-plan-review',
        excerpt: 'Build your own home power generator for under $120 and slash electricity bills by 60% or more with this simple DIY system',
        image: 'https://easydiypowerplan.com/wp-content/uploads/2019/12/easy4-11.jpg'
      },
      {
        title: 'Quick Power System Review',
        slug: 'quick-power-system-review',
        excerpt: 'Tesla-inspired spinning core technology you can build in under 2 hours to achieve energy independence and cut bills by 60-70%',
        image: 'https://quickpowersystem.com/wp-content/uploads/2025/07/cover-1.png'
      },
      {
        title: 'Infinite Energy System Review',
        slug: 'infinite-energy-system-review',
        excerpt: 'Discover John Worrell Keely\'s suppressed 100-year-old invention adapted for modern use - build for under $210 and save 50-70% on electricity',
        image: 'https://readdy.ai/api/search-image?query=Infinite%20energy%20system%20device%20showing%20multiple%20energy%20sources%20and%20modern%20technology%20design%20compact%20home%20power%20generation%20setup%20professional%20product%20photography%20realistic%20high%20quality&width=800&height=600&seq=infinite-energy-system-001&orientation=landscape'
      },
      {
        title: 'The Ultimate Energizer Review',
        slug: 'the-ultimate-energizer-review',
        excerpt: 'Build a Tesla-inspired energy generator for under $208 and save 50-67% on electricity bills with this proven DIY system',
        image: 'https://readdy.ai/api/search-image?query=Tesla%20inspired%20energy%20generator%20device%20on%20workbench%20with%20blueprints%20and%20tools%20DIY%20home%20power%20system%20alternative%20energy%20solution%20professional%20workshop%20photography%20realistic%20high%20quality&width=800&height=600&seq=the-ultimate-energizer-001&orientation=landscape'
      },
      {
        title: 'Home Grid Freedom Review',
        slug: 'home-grid-freedom-review',
        excerpt: 'Military-grade HCPV technology that\'s 500x more efficient than traditional solar - build for under $250 and slash energy costs by up to 93%',
        image: 'https://readdy.ai/api/search-image?query=military%20grade%20HCPV%20solar%20technology%20compact%20home%20energy%20system%20high%20efficiency%20solar%20panels%20modern%20alternative%20energy%20device%20professional%20product%20photography%20realistic%20high%20quality&width=800&height=600&seq=home-grid-freedom-001&orientation=landscape'
      },
      {
        title: 'Tyranny Liberator Review',
        slug: 'tyranny-liberator-review',
        excerpt: 'Compact energy storage system perfect for limited spaces - build for under $200 and cut bills by 70% with this apartment-friendly solution',
        image: 'https://readdy.ai/api/search-image?query=compact%20portable%20energy%20storage%20system%20small%20apartment%20friendly%20power%20device%20space%20saving%20alternative%20energy%20solution%20modern%20clean%20design%20professional%20product%20photography%20realistic%20high%20quality&width=800&height=600&seq=tyranny-liberator-001&orientation=landscape'
      },
      {
        title: 'Tornado Generator Review',
        slug: 'tornado-generator-review',
        excerpt: 'Water-powered hydrogen generator you can build for under $100 to reduce electricity bills by 60-100% with weather-proof operation',
        image: 'https://easypowerguides.info/images/tornado-cover.png'
      },
      {
        title: 'Stirling Free Power Review',
        slug: 'stirling-free-power-review',
        excerpt: 'Dr. Robert Stirling\'s 200-year-old hot-air engine technology - build for under $100 and slash bills by up to 79.8% with NASA-proven design',
        image: 'https://stirlingfreepower.com/wp-content/uploads/2024/09/cover-stirling-2-1024x640.png'
      },
      {
        title: 'Ground Power Generator Review',
        slug: 'ground-power-generator-review',
        excerpt: 'Harvest Earth\'s natural electrical energy - build for under $78 and cut electricity bills by up to 90% with 24/7 operation in all weather',
        image: 'https://groundpowergenerator.online/img/tsl/powerbox.jpg'
      },
      {
        title: 'DIY Dish System Review',
        slug: 'diy-dish-system-review',
        excerpt: 'NASA-inspired solar technology that boosts panel efficiency by 400% - build for around $200 and slash bills by 60% or more',
        image: 'https://diydishsystem.com/wp-content/uploads/2020/04/dish4-sss.jpg'
      },
      {
        title: 'The Freedom Box Review',
        slug: 'the-freedom-box-review',
        excerpt: 'Compact energy system perfect for apartments - build for under $200 and cut bills by 70% with silent, maintenance-free operation',
        image: 'https://the-freedom-box.com/wp-content/uploads/2019/07/BULK_880x517.png'
      }
    ]
  },
  {
    name: 'Conservation & Efficiency',
    slug: 'conservation-efficiency',
    icon: 'ri-recycle-line',
    description: 'Resource conservation and efficiency solutions for sustainable living',
    reviews: [
      {
        title: 'Septifix Review',
        slug: 'septifix-review',
        excerpt: 'Natural septic tank treatment tablets that eliminate odors, prevent backups, and save thousands on pumping costs',
        image: 'https://readdy.ai/api/search-image?query=eco-friendly%20septic%20system%20maintenance%20with%20natural%20treatment%20tablets%20green%20grass%20backyard%20underground%20septic%20tank%20diagram%20clean%20environment%20sustainable%20home%20care%20professional%20photography%20realistic%20high%20quality&width=800&height=600&seq=septifix-001&orientation=landscape'
      },
      {
        title: 'EZ Battery Reconditioning Review',
        slug: 'ez-battery-reconditioning-review',
        excerpt: 'Restore dead batteries to 100% capacity and save thousands with this proven step-by-step reconditioning method for all battery types',
        image: 'https://readdy.ai/api/search-image?query=battery%20reconditioning%20process%20showing%20car%20battery%20being%20restored%20with%20tools%20and%20testing%20equipment%20DIY%20home%20workshop%20setup%20sustainable%20recycling%20solution%20professional%20photography%20realistic%20high%20quality&width=800&height=600&seq=ez-battery-reconditioning-001&orientation=landscape'
      }
    ]
  }
];

// Subcategories for Digital Business & Marketing
const digitalBusinessMarketingSubcategories = [
  {
    name: 'Affiliate Marketing',
    slug: 'affiliate-marketing',
    icon: 'ri-links-line',
    description: 'Proven affiliate marketing systems and training programs',
    reviews: [
      {
        title: 'Write App Reviews Review',
        slug: 'write-app-reviews-review',
        excerpt: 'Earn $25-$50 per app review working from home with this legitimate opportunity for flexible income',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7368ed45ea8c3466731c9bf88186fc3e.png'
      },
      {
        title: 'Profit Vault Review',
        slug: 'profit-vault-review',
        excerpt: 'AI-powered affiliate marketing system that automates product selection, content creation, and traffic generation',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/84572fb5ea3c558270db83a18edc6538.png'
      },
      {
        title: 'AI Marketers Club Review',
        slug: 'ai-marketers-club-review',
        excerpt: 'John Crestani\'s comprehensive training program teaching AI-powered affiliate marketing strategies for six-figure income',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/1b5c9120c2f80eb0d097ca7d0338d976.png'
      },
      {
        title: 'Evolution Review',
        slug: 'evolution-review',
        excerpt: 'Ewen Chia\'s simplified affiliate marketing method designed for busy people to generate online income with minimal daily time investment',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/f15236440ebdb3bf439e4b96a8dbdeae.png'
      },
      {
        title: 'Empire Review',
        slug: 'empire-review',
        excerpt: 'Fergal\'s premium coaching program offering personalized mentorship, advanced strategies, and exclusive community for serious affiliate marketers',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/2bfea25ac4f376144ff526382c1f4ced.png'
      }
    ]
  },
  {
    name: 'Article Marketing',
    slug: 'article-marketing',
    icon: 'ri-article-line',
    description: 'Writing opportunities and content creation platforms',
    reviews: [
      {
        title: 'Paid Online Writing Jobs Review',
        slug: 'paid-online-writing-jobs-review',
        excerpt: 'Access hundreds of legitimate writing opportunities and earn money from home with flexible freelance writing gigs',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7368ed45ea8c3466731c9bf88186fc3e.png'
      }
    ]
  },
  {
    name: 'E-commerce Operations',
    slug: 'e-commerce-operations',
    icon: 'ri-store-3-line',
    description: 'Wholesale suppliers and dropshipping resources',
    reviews: [
      {
        title: 'SaleHoo Review',
        slug: 'salehoo-review',
        excerpt: 'Access 8,000+ verified wholesale suppliers and powerful market research tools for building a successful e-commerce business',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/51197bb509d470938740beda0680b218.png'
      }
    ]
  },
  {
    name: 'Email Marketing',
    slug: 'email-marketing',
    icon: 'ri-mail-line',
    description: 'Email marketing systems and list building strategies',
    reviews: [
      {
        title: 'Profit Fundamentals Review',
        slug: 'profit-fundamentals-review',
        excerpt: 'Master email marketing fundamentals and build sustainable online income through proven list building and monetization strategies',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/9e3459bc0f37d4f6c172f96060e3a347.png'
      }
    ]
  },
  {
    name: 'Marketing',
    slug: 'marketing',
    icon: 'ri-megaphone-line',
    description: 'Online marketing and business development tools',
    reviews: [
      {
        title: 'Secrets of the BIG Dogs Review',
        slug: 'secrets-of-the-big-dogs-review',
        excerpt: 'Learn proven strategies to build wealth during economic downturns and turn recession challenges into profitable opportunities',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6d0e98bed82d8399c6b913a5e07ce93d.png'
      },
      {
        title: 'IDPLR Review',
        slug: 'idplr-review',
        excerpt: 'Access a massive library of 12,000+ ready-to-use digital products with full PLR rights to rebrand and sell as your own',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/e67add09e29536e5c6d936e753636fcf.png'
      },
      {
        title: 'Go Digital Income Review',
        slug: 'go-digital-income-review',
        excerpt: 'Learn multiple proven strategies to build diversified online income streams from affiliate marketing to digital products and freelancing',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/1952ca74f9267fb0b82c1ccb5c7c2127.png'
      },
      {
        title: 'EZ Cover Maker Review',
        slug: 'ez-cover-maker-review',
        excerpt: 'Create stunning 3D ebook covers and product mockups in minutes with no design experience required using professional templates',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7c2f5c575c4816aa5b2210b3345487dd.png'
      },
      {
        title: 'Commissionable Review',
        slug: 'commissionable-review',
        excerpt: 'Complete affiliate marketing system with done-for-you funnels, email sequences, and training to start earning commissions quickly',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/231e72138c9d0424997ed5546d7184e7.png'
      }
    ]
  },
  {
    name: 'Social Media Marketing',
    slug: 'social-media-marketing',
    icon: 'ri-chat-smile-2-line',
    description: 'Social media customer support and engagement tools',
    reviews: [
      {
        title: 'Live Chat Jobs Review',
        slug: 'live-chat-jobs-review',
        excerpt: 'Work from home as a social media customer support representative with flexible hours',
        image: 'https://readdy.ai/api/search-image?query=professional%20customer%20service%20representative%20working%20on%20laptop%20with%20chat%20interface%20and%20headset%20in%20modern%20home%20office%20setup%20with%20clean%20white%20background%20showing%20remote%20work%20environment&width=400&height=300&seq=live-chat-jobs-001&orientation=landscape'
      },
      {
        title: 'Get Paid To Use Social Media Review',
        slug: 'get-paid-social-media-review',
        excerpt: 'Turn your Facebook, Twitter, and YouTube time into income with paid social media positions',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/ac8dfff3dc8ad6a784755fe8a3a2a740.png'
      },
      {
        title: '2026 Social Media Secrets Review',
        slug: '2026-social-media-secrets-review',
        excerpt: 'Master cutting-edge social media strategies across all major platforms with proven growth tactics',
        image: 'https://readdy.ai/api/search-image?query=futuristic%20social%20media%20marketing%20dashboard%20showing%20multiple%20platform%20analytics%20Instagram%20Facebook%20TikTok%20YouTube%20with%20engagement%20metrics%20and%20growth%20charts%20modern%20digital%20marketing%20workspace&width=400&height=300&seq=social-media-secrets-001&orientation=landscape'
      }
    ]
  },
  {
    name: 'Video Marketing',
    slug: 'video-marketing',
    icon: 'ri-video-line',
    description: 'Video creation and marketing tools',
    reviews: [
      {
        title: 'InstaDoodle',
        slug: 'instadoodle-review',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/91b9166eb204155a1b6a8da0591114e1.png',
        excerpt: 'Create stunning doodle videos and graphics with AI-powered design tools.',
        rating: 4.6,
        price: '$37',
      },
      {
        title: 'Speechelo Review',
        slug: 'speechelo-review',
        excerpt: 'Generate natural-sounding AI voiceovers in 3 clicks with 30+ voices',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/31b40e749bc734a5f38509edbea1f39e.png'
      },
      {
        title: 'Videly Review',
        slug: 'videly-review',
        excerpt: 'Rank YouTube videos on page 1 of Google and YouTube with powerful SEO tools',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/292ab2c8b381cf60b882f962e4320072.png'
      }
    ]
  },
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-global-line',
    description: 'Online income opportunities and digital business strategies',
    reviews: [
      {
        title: 'Click Earners Review',
        slug: 'click-earners-review',
        excerpt: 'Earn money online by completing simple tasks like clicking ads, testing websites, and completing surveys with no special skills required',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/1af5322acbf6ec58e8b27b2314351de5.png'
      },
      {
        title: 'Recession Profit Secrets Review',
        slug: 'recession-profit-secrets-review',
        excerpt: 'Learn proven strategies to build wealth during economic downturns and turn recession challenges into profitable opportunities',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6d0e98bed82d8399c6b913a5e07ce93d.png'
      },
      {
        title: 'IDPLR Review',
        slug: 'idplr-review',
        excerpt: 'Access a massive library of 12,000+ ready-to-use digital products with full PLR rights to rebrand and sell as your own',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/5a114fa2c8fa7436a7acaf0ca09f97d6.webp'
      },
      {
        title: 'Go Digital Income Review',
        slug: 'go-digital-income-review',
        excerpt: 'Learn multiple proven strategies to build diversified online income streams from affiliate marketing to digital products and freelancing',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/1952ca74f9267fb0b82c1ccb5c7c2127.png'
      },
      {
        title: 'EZ Cover Maker Review',
        slug: 'ez-cover-maker-review',
        excerpt: 'Create stunning 3D ebook covers and product mockups in minutes with no design experience required using professional templates',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7c2f5c575c4816aa5b2210b3345487dd.png'
      },
      {
        title: 'Commissionable Review',
        slug: 'commissionable-review',
        excerpt: 'Complete affiliate marketing system with done-for-you funnels, email sequences, and training to start earning commissions quickly',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/231e72138c9d0424997ed5546d7184e7.png'
      }
    ]
  }
];

// Subcategories for Family & Parenting
const familyParentingSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-parent-line',
    description: 'Comprehensive family and parenting resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for News & Current Affairs
const newsCurrentAffairsSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-newspaper-line',
    description: 'Comprehensive news and current affairs resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Guides & Resources
const guidesResourcesSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-guide-line',
    description: 'Comprehensive guides and resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Personal Growth
const personalGrowthSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-seedling-line',
    description: 'Comprehensive personal growth resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Software, Tools & Services
const softwareToolsServicesSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-tools-line',
    description: 'Comprehensive software, tools, and services resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Spiritual Life
const spiritualLifeSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-sparkling-line',
    description: 'Comprehensive spiritual resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Mind, Energy & Alternative Paths
const mindEnergyAlternativePathsSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-mind-map',
    description: 'Comprehensive mind and energy resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Sports & Performance
const sportsPerformanceSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-football-line',
    description: 'Comprehensive sports resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

// Subcategories for Travel & Adventure
const travelAdventureSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-plane-line',
    description: 'Comprehensive travel resources',
    reviews: [
      {
        title: 'The Smoothie Diet Review',
        slug: 'the-smoothie-diet-review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        title: 'SleepLean Review',
        slug: 'sleeplean-review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        title: 'BellyFlush™ Review',
        slug: 'bellyflush-review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      }
    ]
  }
];

const healthFitnessWellnessSubcategories = [
  {
    name: 'Exercise & Fitness',
    slug: 'exercise-fitness',
    icon: 'ri-run-line',
    description: 'Fitness programs and exercise systems for strength, balance, and mobility',
    reviews: [
      {
        slug: 'balmorex-review',
        title: 'Balmorex Pro Review',
        excerpt: 'Fast-acting topical pain relief cream with natural ingredients for joint and muscle discomfort',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/50b4527fca04623f4f0f14194ca98158.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'neuro-balance-therapy-review',
        title: 'Neuro-Balance Therapy Review',
        excerpt: 'Revolutionary fall prevention program using a simple 10-second daily ritual to reactivate the deep peroneal nerve and restore balance in seniors',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/1a3afa6607af3165a07433091c828b6c.jpeg',
        rating: 4.8,
        price: '$47'
      }
    ]
  },
  {
    name: 'Meditation',
    slug: 'meditation',
    icon: 'ri-mental-health-line',
    description: 'Meditation and mindfulness programs for stress relief and mental wellness',
    reviews: [
      {
        slug: 'the-edison-wave-review',
        title: 'The Edison Wave Review',
        excerpt: 'Revolutionary tinnitus relief through sound therapy that retrains your brain to eliminate ear ringing naturally',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/78d65fce66ba8187122da890104fe186.png',
        rating: 4.8,
        price: 'One-time'
      }
    ]
  },
  {
    name: 'Remedies',
    slug: 'remedies',
    icon: 'ri-medicine-bottle-line',
    description: 'Natural remedies and holistic health solutions for common conditions',
    reviews: [
      {
        slug: 'the-blood-pressure-program-review',
        title: 'The Blood Pressure Program Review',
        excerpt: 'Natural blood pressure management through simple 9-minute daily exercises that address the root causes of hypertension',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/f46f8dd6522befeb50e20367cf9119c5.webp',
        rating: 4.7,
        price: 'One-time'
      },
      {
        slug: 'bone-density-solution-review',
        title: 'The Bone Density Solution Review',
        excerpt: 'A natural, drug-free program by Blue Heron Health News designed to help reverse osteoporosis and rebuild strong, healthy bones',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e43dee17ce855d107b2bba46b2b0ec3.jpeg',
        rating: 4.7,
        price: 'One-time'
      },
      {
        slug: 'the-parkinsons-protocol-review',
        title: "The Parkinson's Protocol Review",
        excerpt: "A comprehensive natural program designed to help manage Parkinson's symptoms through targeted dietary, exercise, and lifestyle strategies",
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/713d0536f01f0087698a4e45ba5cab88.png',
        rating: 4.8,
        price: 'One-time'
      }
    ]
  },
  {
    name: 'Dietary Supplements',
    slug: 'dietary-supplements',
    icon: 'ri-capsule-line',
    description: 'Natural supplements for weight loss, metabolism, and overall wellness',
    reviews: [
      {
        slug: 'neuro-thrive-review',
        title: 'Neuro-Thrive Review',
        excerpt: 'Advanced brain health supplement with 7 powerful natural ingredients that support memory, focus, and cognitive function',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/97862f62c142cca778e05c30a29aab61.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'mitolyn-review',
        title: 'MITOLYN Review',
        excerpt: 'Breakthrough formula that supports healthy mitochondria levels for natural weight loss and sustained energy',
        image: 'https://mitolyn.com/assets/images/product-home.png',
        rating: 4.8,
        price: 'From $59'
      },
      {
        slug: 'zencortex-review',
        title: 'ZenCortex Review',
        excerpt: 'Revolutionary hearing support formula with 20+ natural ingredients that protect and enhance auditory health naturally',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3d8f5bc728b8299f1aab980bb371b5f3.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'citrusburn-review',
        title: 'CitrusBurn Review',
        excerpt: 'Spanish fat-melting citrus formula — burn more, crave less, and feel great all day',
        image: 'https://citrusburn.com/images/bnrbxrgtprd.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'audifort-review',
        title: 'Audifort Review',
        excerpt: 'Support your hearing naturally with this advanced formula designed to promote healthy auditory function',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6879c64b51561166c02fd803e198f637.png',
        rating: 4.6,
        price: 'From $49'
      },
      {
        slug: 'gluco6-review',
        title: 'Gluco6 Review',
        excerpt: 'Revolutionary blood sugar support formula that helps maintain healthy glucose levels and promotes metabolic wellness',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/e7d38d42f53e467982a7039e085627ae.webp',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'leanbiome-review',
        title: 'LeanBiome Review',
        excerpt: 'Revolutionary gut microbiome formula with 9 clinically-researched probiotic strains targeting the root cause of belly fat',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/ba91a943f65d0f3e06136ad058bb5425.webp',
        rating: 4.7,
        price: 'From $59'
      },
      {
        slug: 'gut-vita-review',
        title: 'Gut Vita Review',
        excerpt: 'Natural gut health supplement supporting digestive wellness, healthy gut flora, and overall digestive comfort',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6879c64b51561166c02fd803e198f637.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'sugarmute-review',
        title: 'SugarMute Review',
        excerpt: 'Advanced blood sugar support supplement with natural ingredients to help maintain healthy glucose levels',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/733aba0c6b7348987cf7ae792da4ea23.jpeg',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'glucoberry-review',
        title: 'GlucoBerry Review',
        excerpt: 'Doctor-formulated blood sugar support with premium maqui berry extract to activate your body\'s natural Blood Sugar Drain',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/4765e49df20ebc6022d7f09094a3f2a2.png',
        rating: 4.8,
        price: 'From $39'
      },
      {
        slug: 'jointvive-review',
        title: 'JointVive Review',
        excerpt: 'Natural joint support supplement with glucosamine, chondroitin, MSM, and turmeric to reduce pain and improve mobility',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6879c64b51561166c02fd803e198f637.png',
        rating: 4.8,
        price: 'From $59'
      },
      {
        slug: 'nagano-tonic-review',
        title: 'Nagano Tonic Review',
        excerpt: 'Japanese-inspired lean body tonic with powerful superfoods that support metabolism, boost energy, and help achieve natural weight loss',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/136317495de77836316340edcf4f1bfc.png',
        rating: 4.8,
        price: 'From $39'
      },
      {
        slug: 'prosta-peak-review',
        title: 'Prosta Peak Review',
        excerpt: 'Natural prostate support supplement with 20+ plant-based ingredients to promote healthy urinary function and male wellness',
        image: 'https://prostapeak.com/images/postapeak/1bottle.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'gluco-extend-review',
        title: 'Gluco Extend Review',
        excerpt: 'Natural blood sugar support formula with a proprietary blend of plants and minerals to maintain healthy glucose levels',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/23cbdc3d207f461f8393f79fc9ec4671.webp',
        rating: 4.6,
        price: 'From $49'
      },
      {
        slug: 'arcticblast-review',
        title: 'ArcticBlast Review',
        excerpt: 'Fast-acting pain relief drops with cooling menthol and natural ingredients for joint and muscle discomfort',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/733aba0c6b7348987cf7ae792da4ea23.jpeg',
        rating: 4.7,
        price: 'From $59'
      },
      {
        slug: 'hepatoburn-review',
        title: 'HepatoBurn Review',
        excerpt: 'Liver-supporting weight loss formula that targets stubborn fat by optimizing liver function and metabolism',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/fd97af896521db7cee1e91a7fd69f5f4.jpeg',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'pineal-guardian-x-review',
        title: 'Pineal Guardian X Review',
        excerpt: 'Brain detox formula designed to flush toxic fluoride from your pineal gland and restore natural melatonin production for enhanced memory and cognitive function',
        image: 'https://thepinealguardian.com/assets-mbn/images/pgr-prod-1.avif',
        rating: 4.8,
        price: 'From $39'
      },
      {
        slug: 'liv-pure-review',
        title: 'Liv Pure Review',
        excerpt: 'Mediterranean liver purification formula that supports detoxification and fat-burning by optimizing liver function',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/50e205a42958e3415a4b17f77e712dee.jpeg',
        rating: 4.9,
        price: 'From $49'
      },
      {
        slug: 'ikaria-juice-review',
        title: 'Ikaria Juice Review',
        excerpt: 'Ancient metabolism-boosting juice formula with 8 powerful nutrients that target ceramides to support fat loss',
        image: 'https://theikariajuice.com/new-home/images/single-product.png',
        rating: 4.7,
        price: 'From $39'
      },
      {
        slug: 'neuro-serge-review',
        title: 'Neuro Serge Review',
        excerpt: 'Advanced brain health formula with 20+ natural ingredients designed to support cognitive function and mental clarity',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3d8f5bc728b8299f1aab980bb371b5f3.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'cardio-slim-tea-review',
        title: 'Cardio Slim Tea Review',
        excerpt: 'Natural tea blend with 15 heart-supporting ingredients that promote cardiovascular health and support healthy weight management',
        image: 'https://lh3.googleusercontent.com/3_9LLCkspYru8NlzxnmPOodoM2BG9WOg8Gy-Zt94fj-TIrK8dbOQqUsi_TincA7Ar0fu3If7BmA-xoo1aACpgi3OPLp368DBYg',
        rating: 4.9,
        price: 'From $49'
      },
      {
        slug: 'finessa-review',
        title: 'Finessa Review',
        excerpt: 'Revolutionary gut health formula targeting the gut-liver axis to support smooth digestion and a flatter belly naturally',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/23cbdc3d207f461f8393f79fc9ec4671.webp',
        rating: 4.9,
        price: 'From $39'
      },
      {
        slug: 'java-burn-review',
        title: 'Java Burn Review',
        excerpt: 'Revolutionary coffee booster that supercharges your metabolism and accelerates fat burning when mixed with your morning coffee',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/2f3c08fcba2984193669226e2bcb375d.png',
        rating: 4.7,
        price: 'From $34'
      },
      {
        slug: 'igenics-review',
        title: 'iGenics Review',
        excerpt: 'Plant-based vision support formula with 12 clinically-backed ingredients that promote healthier eyesight',
        image: 'https://visionbreakthrough.com/img/img1.png',
        rating: 4.6,
        price: 'From $59'
      },
      {
        slug: 'sugar-defender-review',
        title: 'Sugar Defender Review',
        excerpt: 'Natural blood sugar support formula with 8 carefully-selected ingredients that help maintain healthy glucose levels',
        image: 'https://sugardefender24.com/assets/img/SUGARDEFENDERx6-500px.png',
        rating: 4.8,
        price: 'From $69'
      },
      {
        slug: 'metabo-drops-review',
        title: 'Metabo Drops Review',
        excerpt: 'Liquid metabolism booster with 8 natural ingredients including African Mango, Guarana, and L-Carnitine',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/daa0c08047044f7ca21afe84d1d236d0.png',
        rating: 4.7,
        price: 'From $49'
      }
    ]
  },
  {
    name: 'Diets & Weight Loss',
    slug: 'diets-weight-loss',
    icon: 'ri-heart-pulse-line',
    description: 'Proven weight loss programs and dietary solutions',
    reviews: [
      {
        slug: 'venoplus-8-review',
        title: 'VENOPLUS 8 Review',
        excerpt: 'Revolutionary blood circulation support formula with nitric oxide boosters that enhance cardiovascular health and energy',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/6f99325fb21e73c6fac48b43fe2080dc.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'the-smoothie-diet-review',
        title: 'The Smoothie Diet Review',
        excerpt: '21-day rapid weight loss program with delicious smoothie recipes that help you lose weight naturally while boosting energy and improving health',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/086c78a8c1bb72754110d358b91dd3af.png',
        rating: 4.8,
        price: '$37'
      },
      {
        slug: 'sleeplean-review',
        title: 'SleepLean Review',
        excerpt: 'Nighttime metabolism booster that helps you burn fat while you sleep with natural ingredients for better rest and weight management',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/3e132bf22c8786eb664b71bfe8b9d641.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'bellyflush-review',
        title: 'BellyFlush™ Review',
        excerpt: 'Natural digestive support supplement with 11 plant-based ingredients including Cascara Sagrada, Senna Leaf, and Marshmallow Root to promote regularity and gut comfort',
        image: 'https://tgenics-cdn.s3.amazonaws.com/bellyflush/images/cb/img1.jpg',
        rating: 4.7,
        price: 'From $59'
      },
      {
        slug: 'slim-crystal-review',
        title: 'SLIM CRYSTAL Review',
        excerpt: 'Crystal-infused water bottle designed to support natural weight loss and boost metabolism through nine natural crystals',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/c9501406269e7b88c4f8fb91f3b81cf3.png',
        rating: 4.7,
        price: 'From $117'
      },
      {
        slug: '14-day-rapid-soup-diet-review',
        title: '14 Day Rapid Soup Diet Review',
        excerpt: 'Revolutionary keto soup program that helps you lose 10-20 pounds in just 14 days while enjoying delicious, satisfying meals that keep hunger at bay',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/4469ec3a4605e3cec40034d8b3e7dae5.png',
        rating: 4.8,
        price: '$37'
      }
    ]
  },
  {
    name: "Men's Health",
    slug: 'mens-health',
    icon: 'ri-user-heart-line',
    description: "Specialized supplements and solutions for men's health and vitality",
    reviews: [
      {
        slug: 'spartamax-review',
        title: 'Spartamax Review',
        excerpt: 'Premium male enhancement formula with natural ingredients to support testosterone, stamina, and overall masculine vitality',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/9f6a254635f9f495d73206be0928f282.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'titanflow-review',
        title: 'TitanFlow Review',
        excerpt: 'Advanced prostate health supplement targeting urethral wall strength for improved urinary flow and male vitality',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/8db1bbc9d7b2b2d0a158082e22da354e.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'prostavive-review',
        title: 'ProstaVive Review',
        excerpt: 'The most potent, fast-acting formula for prostate health and strong flow — support healthy blood circulation naturally',
        image: 'https://prostavive.org/home-assets/images/product.png',
        rating: 4.7,
        price: 'From $69'
      },
      {
        slug: 'protoflow-review',
        title: 'Protoflow Review',
        excerpt: 'Natural prostate health supplement with 10+ plant-based ingredients including Saw Palmetto and Chinese Ginseng',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7de969d1da7580a86ba333d729312374.png',
        rating: 4.7,
        price: 'From $49'
      }
    ]
  },
  {
    name: "Women's Health",
    slug: 'womens-health',
    icon: 'ri-heart-2-line',
    description: "Specialized supplements and solutions for women's hormonal health and vitality",
    reviews: [
      {
        slug: 'thyrafemme-balance-review',
        title: 'Thyrafemme Balance Review',
        excerpt: 'Natural thyroid support supplement specifically formulated for women to promote healthy thyroid function and balanced hormones',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/84226ff7123d0e770536492e792c37c3.png',
        rating: 4.8,
        price: 'From $49'
      }
    ]
  },
  {
    name: 'Dental Health',
    slug: 'dental-health',
    icon: 'ri-empathize-line',
    description: 'Oral health supplements and dental care solutions',
    reviews: [
      {
        slug: 'prodentim-review',
        title: 'ProDentim Review',
        excerpt: 'Revolutionary probiotic supplement that supports healthy teeth and gums with 3.5 billion beneficial bacteria',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/5d92d5b14737179e70f4402ebda66c89.png'
      },
      {
        slug: 'provadent-review',
        title: 'ProvaDent Review',
        excerpt: 'Advanced dental probiotic formula that supports oral microbiome balance and promotes healthy teeth and gums naturally',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/b59c95caa7a970ac26c8c5897d467de0.webp'
      },
      {
        slug: 'biodentex-review',
        title: 'BioDentex Review',
        excerpt: 'A detailed analysis of this dental health supplement for oral microbiome support and enamel protection',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/549f96ef1555f03571ac5c7ba0af1d68.png'
      }
    ]
  },
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-heart-line',
    description: 'Comprehensive wellness solutions for overall health and vitality',
    reviews: [
      {
        slug: 'neuro-energizer-review',
        title: 'Neuro Energizer Review',
        excerpt: 'Unlock your brain\'s full potential with this 7-second audio program based on Einstein\'s stolen brain research',
        image: 'https://cdn.prod.website-files.com/6752f4c7589e8997cdb008e3/690cbf57b4960256fe924b7a_bundle.webp'
      },
      {
        slug: 'the-memory-wave-review',
        title: 'The Memory Wave Review',
        excerpt: 'Support sharper memory and cognitive clarity with this 12-minute daily audio program using gamma brain wave technology',
        image: 'https://discovermemorywave.com/std/images/prd-img-mini.png'
      }
    ]
  }
];

// Subcategories for Men's Health
const mensHealthSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-user-heart-line',
    description: 'Comprehensive men\'s health resources',
    reviews: [
      {
        slug: 'spartamax-review',
        title: 'Spartamax Review',
        excerpt: 'Premium male enhancement formula with natural ingredients to support testosterone, stamina, and overall masculine vitality',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/9f6a254635f9f495d73206be0928f282.png',
        rating: 4.8,
        price: 'From $49'
      },
      {
        slug: 'titanflow-review',
        title: 'TitanFlow Review',
        excerpt: 'Advanced prostate health supplement targeting urethral wall strength for improved urinary flow and male vitality',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/8db1bbc9d7b2b2d0a158082e22da354e.png',
        rating: 4.7,
        price: 'From $49'
      },
      {
        slug: 'prostavive-review',
        title: 'ProstaVive Review',
        excerpt: 'The most potent, fast-acting formula for prostate health and strong flow — support healthy blood circulation naturally',
        image: 'https://prostavive.org/home-assets/images/product.png',
        rating: 4.7,
        price: 'From $69'
      },
      {
        slug: 'protoflow-review',
        title: 'Protoflow Review',
        excerpt: 'Natural prostate health supplement with 10+ plant-based ingredients including Saw Palmetto and Chinese Ginseng',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/7de969d1da7580a86ba333d729312374.png',
        rating: 4.7,
        price: 'From $49'
      }
    ]
  }
];

// Subcategories for Women's Health
const womensHealthSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-heart-2-line',
    description: 'Comprehensive women\'s health resources',
    reviews: [
      {
        slug: 'thyrafemme-balance-review',
        title: 'Thyrafemme Balance Review',
        excerpt: 'Natural thyroid support supplement specifically formulated for women to promote healthy thyroid function and balanced hormones',
        image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/84226ff7123d0e770536492e792c37c3.png',
        rating: 4.8,
        price: 'From $49'
      }
    ]
  }
];

// Subcategories for General Wellness
const generalWellnessSubcategories = [
  {
    name: 'General',
    slug: 'general',
    icon: 'ri-heart-line',
    description: 'Comprehensive wellness solutions for overall health and vitality',
    reviews: [
      {
        slug: 'neuro-energizer-review',
        title: 'Neuro Energizer Review',
        excerpt: 'Unlock your brain\'s full potential with this 7-second audio program based on Einstein\'s stolen brain research',
        image: 'https://cdn.prod.website-files.com/6752f4c7589e8997cdb008e3/690cbf57b4960256fe924b7a_bundle.webp'
      },
      {
        slug: 'the-memory-wave-review',
        title: 'The Memory Wave Review',
        excerpt: 'Support sharper memory and cognitive clarity with this 12-minute daily audio program using gamma brain wave technology',
        image: 'https://discovermemorywave.com/std/images/prd-img-mini.png'
      }
    ]
  }
];

const healthFitnessProducts = [
  {
    id: 'slim-crystal',
    title: 'SLIM CRYSTAL',
    description: 'Crystal-infused water bottle designed to support natural weight loss, boost metabolism, and enhance hydration through the power of nine natural crystals.',
    image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/c9501406269e7b88c4f8fb91f3b81cf3.png',
    link: '/reviews/slim-crystal-review',
    category: 'Health, Fitness & Wellness',
    subcategory: 'Diets & Weight Loss'
  }
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const categoryData: CategoryData = {
    'culture-creativity': {
      title: 'Culture & Creativity',
      description: 'Explore art, music, design, and creative expression across a wide range of disciplines.',
      icon: 'ri-palette-line',
      subcategories: cultureCreativitySubcategories,
    },
    'smart-betting-strategies': {
      title: 'Smart Betting Strategies',
      description: 'Data-driven insights and proven systems for informed betting decisions across football, horse racing, soccer, and more.',
      icon: 'ri-trophy-line',
      subcategories: smartBettingSubcategories,
    },
    'business-wealth': {
      title: 'Business & Wealth',
      description: 'Build wealth through smart business strategies, trading systems, and financial education.',
      icon: 'ri-line-chart-line',
      subcategories: businessWealthSubcategories,
    },
    'culinary-living': {
      title: 'Culinary Living',
      description: 'Healthy recipes, cooking guides, and kitchen essentials for a delicious and nutritious lifestyle.',
      icon: 'ri-restaurant-line',
      subcategories: [],
    },
    'digital-business-marketing': {
      title: 'Digital Business & Marketing',
      description: 'Master online business and digital marketing with proven systems and training programs.',
      icon: 'ri-megaphone-line',
      subcategories: digitalBusinessMarketingSubcategories,
    },
    'learning-education': {
      title: 'Learning & Education',
      description: 'Resources for lifelong learning and growth across various subjects and skills.',
      icon: 'ri-book-open-line',
      subcategories: [],
    },
    'careers-work-life': {
      title: 'Careers & Work Life',
      description: 'Navigate your professional journey with confidence through career training and work opportunities.',
      icon: 'ri-briefcase-line',
      subcategories: [],
    },
    'eco-living-sustainability': {
      title: 'Eco Living & Sustainability',
      description: 'Live sustainably and protect our planet with renewable energy solutions and eco-friendly products.',
      icon: 'ri-leaf-line',
      subcategories: [],
    },
    'health-fitness-wellness': {
      title: 'Health, Fitness & Wellness',
      description: 'Discover science-backed solutions for better health, sustainable weight management, enhanced cognitive function, and overall wellness. Our comprehensive reviews help you make informed decisions about products that support your health journey.',
      icon: 'ri-heart-pulse-line',
      subcategories: [],
    },
    'stories-fiction': {
      title: 'Stories & Fiction',
      description: 'Discover compelling narratives and creative writing across various genres.',
      icon: 'ri-quill-pen-line',
      subcategories: [],
    },
    'gaming-play': {
      title: 'Gaming & Play',
      description: 'Reviews, guides, and gaming culture insights for enthusiasts and casual players.',
      icon: 'ri-gamepad-line',
      subcategories: [],
    },
    'home-garden-living': {
      title: 'Home, Garden & Living',
      description: 'Create beautiful, functional living spaces with our home and garden guides.',
      icon: 'ri-home-smile-line',
      subcategories: [],
    },
    'languages-communication': {
      title: 'Languages & Communication',
      description: 'Master languages and effective communication skills for personal and professional growth.',
      icon: 'ri-translate-2',
      subcategories: [],
    },
    'mobile-tech-apps': {
      title: 'Mobile Tech & Apps',
      description: 'Stay updated with mobile technology trends and app reviews.',
      icon: 'ri-smartphone-line',
      subcategories: [],
    },
    'family-parenting': {
      title: 'Family & Parenting',
      description: 'Guidance for modern family life and parenting challenges.',
      icon: 'ri-parent-line',
      subcategories: [],
    },
    'news-current-affairs': {
      title: 'News & Current Affairs',
      description: 'Stay informed with curated news analysis and current events coverage.',
      icon: 'ri-newspaper-line',
      subcategories: [],
    },
    'guides-resources': {
      title: 'Guides & Resources',
      description: 'Comprehensive how-to guides and resources for various topics.',
      icon: 'ri-guide-line',
      subcategories: [],
    },
    'personal-growth': {
      title: 'Personal Growth',
      description: 'Transform yourself through continuous improvement and self-development.',
      icon: 'ri-seedling-line',
      subcategories: [],
    },
    'software-tools-services': {
      title: 'Software, Tools & Services',
      description: 'Find the best digital tools and services for your needs.',
      icon: 'ri-tools-line',
      subcategories: [],
    },
    'spiritual-life': {
      title: 'Spiritual Life',
      description: 'Explore spirituality and inner peace through various practices and teachings.',
      icon: 'ri-sparkling-line',
      subcategories: [],
    },
    'mind-energy-alternative-paths': {
      title: 'Mind, Energy & Alternative Paths',
      description: 'Discover alternative approaches to wellbeing and consciousness.',
      icon: 'ri-mind-map',
      subcategories: [],
    },
    'sports-performance': {
      title: 'Sports & Performance',
      description: 'Enhance athletic performance and sports knowledge with expert guidance.',
      icon: 'ri-football-line',
      subcategories: [],
    },
    'travel-adventure': {
      title: 'Travel & Adventure',
      description: 'Explore the world with expert travel guides and adventure tips.',
      icon: 'ri-plane-line',
      subcategories: [],
    },
  };

  const isCultureCreativity = slug === 'culture-creativity';
  const isSmartBetting = slug === 'smart-betting-strategies';
  const isBusinessWealth = slug === 'business-wealth';
  const isCulinaryLiving = slug === 'culinary-living';
  const isDigitalBusinessMarketing = slug === 'digital-business-marketing';
  const isCareersWorkLife = slug === 'careers-work-life';
  const isEcoLiving = slug === 'eco-living-sustainability';
  const isHealthFitnessWellness = slug === 'health-fitness-wellness';

  // Check if category exists in categoryData
  const currentCategory = slug ? categoryData[slug] : null;

  const categorySchema = currentCategory && slug ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${siteUrl}/category/${slug}#webpage`,
    url: `${siteUrl}/category/${slug}`,
    name: `${currentCategory.title} Reviews – Simsers Affiliate`,
    description: currentCategory.description,
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/#categories` },
        { '@type': 'ListItem', position: 3, name: currentCategory.title, item: `${siteUrl}/category/${slug}` },
      ],
    },
    dateModified: '2026-02-08',
  } : null;

  useEffect(() => {
    if (!currentCategory || !slug) return;
    document.title = `${currentCategory.title} Reviews – Simsers Affiliate`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', currentCategory.description);
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute('content', `${currentCategory.title.toLowerCase()}, product reviews, affiliate reviews, ${slug.replace(/-/g, ' ')}`);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/category/${slug}`);
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'index, follow');
  }, [slug, currentCategory]);

  // If category doesn't exist, show error or redirect
  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-20 md:pt-24 pb-12 md:pb-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-12">
            <div className="bg-white rounded-2xl border border-orange-100 p-8 md:p-12 text-center shadow-lg">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-full mx-auto mb-4 md:mb-6">
                <i className="ri-error-warning-line text-3xl md:text-4xl text-orange-600"></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">Category Not Found</h1>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">The category you're looking for doesn't exist.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 md:px-6 py-2.5 md:py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-home-line"></i>
                <span>Back to Homepage</span>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {categorySchema && <SchemaOrg schema={categorySchema} id={`schema-category-${slug}`} />}
      <Header />
      
      <main className="pt-20 md:pt-24 pb-12 md:pb-20 relative overflow-hidden">
        {/* Colorful gradient background like homepage */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50"></div>
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=subtle%20abstract%20geometric%20pattern%20warm%20beige%20cream%20tones%20minimalist%20texture%20soft%20elegant%20background%20seamless%20pattern%20light%20neutral%20colors%20modern%20design%20aesthetic&width=1920&height=1080&seq=catbg001&orientation=landscape')] bg-cover bg-center opacity-20"></div>
        
        {/* Decorative blurred circles */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-orange-300/30 to-amber-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-gradient-to-br from-rose-300/20 to-orange-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-amber-200/20 to-teal-200/10 rounded-full blur-3xl"></div>

        <article className="max-w-6xl mx-auto px-4 md:px-6 lg:px-12 relative z-10">
          {/* Breadcrumb */}
          <nav className="mb-6 md:mb-8 text-sm md:text-base">
            <ol className="flex items-center gap-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li>
                <Link to="/#categories" className="hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                  Categories
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-gray-900 font-medium truncate">{slug}</li>
            </ol>
          </nav>

          {/* Category Header */}
          <header className="mb-8 md:mb-12">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl mb-4 md:mb-6 shadow-lg shadow-orange-200/50">
              <i className={`${currentCategory.icon} text-3xl md:text-4xl text-orange-600`}></i>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-inter">
              {currentCategory.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              {currentCategory.description}
            </p>
          </header>

          {/* Category Introduction */}
          <section className="bg-white/80 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 mb-10 md:mb-16 shadow-lg shadow-orange-100/50">
            {isSmartBetting ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review proven betting systems, tipster services, and prediction tools designed to help you make smarter, more profitable wagers. Whether you're interested in football, horse racing, soccer, or multi-sport strategies, the products featured here aim to solve common betting challenges: finding value, managing risk, and improving long-term profitability.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each system we review has been evaluated based on its methodology, track record, ease of use, and real-world performance. From lay betting formulas and matched betting platforms to statistical prediction models and expert tipster networks, we break down what works, what doesn't, and who each product is best suited for—whether you're a beginner looking for guidance or an experienced bettor seeking an edge.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you compare your options and choose a betting strategy that fits your goals, budget, and risk tolerance. Scroll down to explore detailed reviews organized by sport and betting style, complete with pros, cons, and honest assessments from our research.
                </p>
              </>
            ) : isBusinessWealth ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review proven systems, courses, and platforms designed to help you build wealth, start profitable businesses, and achieve financial independence. Whether you're interested in trading and investing, launching a home-based business, or accessing government grants and financial assistance, the products featured here aim to solve real financial challenges and create sustainable income streams.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each product we review has been evaluated based on training quality, earning potential, ease of implementation, and real member results. From beginner-friendly affiliate programs and AI-powered marketing systems to advanced scaling strategies taught by proven experts, we break down what works, what doesn't, and who each product is best suited for—whether you're just starting out or looking to take your online business to the next level.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you choose legitimate opportunities that match your skills, budget, and income goals. Scroll down to explore detailed reviews organized by business model and expertise level, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : isCulinaryLiving ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review cookbooks, recipe collections, and nutrition guides designed to help you eat healthier, cook smarter, and transform your relationship with food. Whether you're interested in fat-burning nutrition, plant-based eating, or keto-friendly baking, the products featured here aim to solve common kitchen challenges: finding delicious healthy recipes, understanding nutrition science, and making sustainable dietary changes.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each product we review has been evaluated based on recipe quality, nutritional value, ease of preparation, and real-world results. From metabolism-boosting meal plans and comprehensive vegan cookbooks to specialized keto dessert collections, we break down what works, what doesn't, and who each product is best suited for—whether you're a complete beginner or an experienced home chef looking to expand your repertoire.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you find cooking resources that match your dietary goals, lifestyle needs, and budget. Scroll down to explore detailed reviews organized by cooking style and dietary focus, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : isDigitalBusinessMarketing ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review proven online business systems, affiliate marketing training programs, and digital marketing tools designed to help you build profitable internet businesses. Whether you're interested in affiliate marketing, content creation, or leveraging AI for business automation, the products featured here aim to solve common challenges: generating online income, building sustainable traffic sources, and scaling digital businesses.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each product we review has been evaluated based on training quality, earning potential, ease of implementation, and real member results. From beginner-friendly affiliate programs and AI-powered marketing systems to advanced scaling strategies taught by proven experts, we break down what works, what doesn't, and who each product is best suited for—whether you're just starting out or looking to take your online business to the next level.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you choose legitimate opportunities that match your skills, budget, and income goals. Scroll down to explore detailed reviews organized by business model and expertise level, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : isCareersWorkLife ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review career training programs, professional development courses, and work-from-home opportunities designed to help you advance your career, develop new skills, and achieve better work-life balance. Whether you're looking to start a new career, gain specialized training, or find flexible work opportunities, the products featured here aim to solve common career challenges: finding legitimate opportunities, acquiring marketable skills, and building sustainable income.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each program we review has been evaluated based on training quality, earning potential, flexibility, and real-world career outcomes. From specialized career training and certification programs to flexible work-from-home opportunities, we break down what works, what doesn't, and who each program is best suited for—whether you're starting fresh, changing careers, or looking to enhance your current professional skills.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you make informed decisions about career opportunities and training programs that match your skills, lifestyle needs, and professional goals. Scroll down to explore detailed reviews organized by career field and opportunity type, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : isEcoLiving ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review sustainable living solutions, renewable energy systems, and eco-friendly products designed to help you reduce your environmental impact while saving money. Whether you're interested in alternative energy, conservation strategies, or sustainable home solutions, the products featured here aim to solve common challenges: reducing utility costs, minimizing waste, and living more sustainably.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each product we review has been evaluated based on environmental impact, cost savings, ease of implementation, and real-world effectiveness. From DIY renewable energy systems and natural conservation solutions to eco-friendly home maintenance products, we break down what works, what doesn't, and who each product is best suited for—whether you're just starting your sustainability journey or looking to take your eco-friendly lifestyle to the next level.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you make informed decisions about sustainable living solutions that align with your environmental values, budget, and lifestyle. Scroll down to explore detailed reviews organized by sustainability focus, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : isHealthFitnessWellness ? (
              <>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Welcome to our <strong>{currentCategory.title}</strong> hub, where we review proven supplements, health systems, and wellness products designed to help you optimize your physical health, boost energy levels, and achieve your fitness goals. Whether you're interested in weight loss supplements, men's health solutions, or overall wellness support, the products featured here aim to solve common health challenges: improving metabolism, supporting vital organ function, and enhancing overall vitality.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                  Each product we review has been evaluated based on ingredient quality, scientific backing, user results, and safety profile. From metabolism-boosting supplements and prostate health formulas to natural wellness solutions, we break down what works, what doesn't, and who each product is best suited for—whether you're just starting your health journey or looking to optimize your existing wellness routine.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Our goal is to help you make informed decisions about health products that align with your wellness goals, lifestyle, and budget. Scroll down to explore detailed reviews organized by health focus, complete with pros, cons, usage tips, and honest assessments from our research.
                </p>
              </>
            ) : (
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                {currentCategory.description}
              </p>
            )}
          </section>

          {/* Subcategories for Culture & Creativity */}
          {isCultureCreativity ? (
            <section className="space-y-10 md:space-y-16">
              {cultureCreativitySubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isSmartBetting ? (
            <section className="space-y-10 md:space-y-16">
              {smartBettingSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isBusinessWealth ? (
            <section className="space-y-10 md:space-y-16">
              {businessWealthSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isCulinaryLiving ? (
            <section className="space-y-10 md:space-y-16">
              {/* Cooking Subcategory */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                    <i className="ri-fire-line text-2xl md:text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Cooking</h2>
                    <p className="text-base md:text-lg text-gray-600">Nutrition guides and healthy cooking systems</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {culinaryLivingProducts
                    .filter(product => product.subcategory === 'Cooking')
                    .map((product) => (
                      <Link
                        key={product.id}
                        to={product.link}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-3 md:p-4">
                          <img
                            src={product.image}
                            alt={product.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {product.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {product.description}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>

              {/* Recipes Subcategory */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                    <i className="ri-book-2-line text-2xl md:text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">Recipes</h2>
                    <p className="text-base md:text-lg text-gray-600">Recipe collections and specialized cookbooks</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {culinaryLivingProducts
                    .filter(product => product.subcategory === 'Recipes')
                    .map((product) => (
                      <Link
                        key={product.id}
                        to={product.link}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-3 md:p-4">
                          <img
                            src={product.image}
                            alt={product.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {product.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {product.description}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </section>
          ) : isDigitalBusinessMarketing ? (
            <section className="space-y-10 md:space-y-16">
              {digitalBusinessMarketingSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isCareersWorkLife ? (
            <section className="space-y-10 md:space-y-16">
              {careersWorkLifeSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isEcoLiving ? (
            <section className="space-y-10 md:space-y-16">
              {ecoLivingSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : isHealthFitnessWellness ? (
            <section className="space-y-10 md:space-y-16">
              {healthFitnessWellnessSubcategories.map((subcategory) => (
                <div key={subcategory.slug} className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-5 md:p-8 lg:p-10 shadow-xl shadow-orange-100/30">
                  {/* Subcategory Header */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex-shrink-0 shadow-md shadow-orange-200/50">
                      <i className={`${subcategory.icon} text-2xl md:text-3xl text-orange-600`}></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{subcategory.name}</h2>
                      <p className="text-base md:text-lg text-gray-600">{subcategory.description}</p>
                    </div>
                  </div>

                  {/* Reviews Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {subcategory.reviews.map((review) => (
                      <Link
                        key={review.slug}
                        to={`/reviews/${review.slug}`}
                        className="group bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-white p-4">
                          <img
                            src={review.image}
                            alt={review.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {review.title}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600 line-clamp-2 mb-3 md:mb-4">
                            {review.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-orange-600 text-sm md:text-base font-medium">
                            <span>Read Review</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : (
            // Placeholder for other categories
            <section className="bg-white/90 backdrop-blur-sm rounded-2xl border border-orange-100 p-8 md:p-12 text-center shadow-lg shadow-orange-100/30">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-gradient-to-br from-orange-100 to-gray-700 rounded-full mx-auto mb-4 md:mb-6">
                <i className="ri-article-line text-3xl md:text-4xl text-orange-500"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2 md:mb-3">
                Content Coming Soon
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 max-w-md mx-auto">
                We're currently developing comprehensive guides and reviews for this category. Check back soon for expert insights and recommendations.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 md:px-6 py-2.5 md:py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-home-line"></i>
                <span>Back to Homepage</span>
              </Link>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
